import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import TextInput from "../../Global Functions/textInput";
import { Link, useNavigate, useParams } from "react-router-dom";
import SelectInput from "../../Global Functions/selectInput";
import InnerHeading from "../../Global Functions/innerHeading";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewDeal,
  updateDeal,
  viewDeal,
} from "../../Redux/Actions/dealsAction";
import { getEnums } from "../../Redux/Actions/enumsActions";
import AddImage from "../../Global Functions/addImage";
import {
  viewCityList,
  viewStateList,
} from "../../Redux/Actions/settingsActions";
import { getProjectList } from "../../Redux/Actions/projectActions";
import ImageShowerModalGlobal from "../../Global Functions/imageShowerModalGlobal";
import toast from "react-hot-toast";
import { viewBuilderList } from "../../Redux/Actions/builderActions";
import "./DealForm.css";

const DealForm = () => {
  const [dealsData, setDealsData] = useState({
    name: "",
    image: "",
    title: "",
    builder_id: "",
    discount_type: "",
    deal_type: "",
    country_id: "",
    state: "",
    city: "",
    project_id: "",
    value: "",
    start_date: "",
    end_date: "",
    term_and_condition: "",
    is_special: false,
  });

  const [originalDealData, setOriginalDealData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { deal } = useSelector((state) => state.deals || {});
  const { builders } = useSelector((state) => state.builders || {});
  const { enumsData } = useSelector((state) => state.enumsData || {});
  const { countryList } = useSelector((state) => state.country || {});
  const { states } = useSelector((state) => state.states || {});
  const { cities } = useSelector((state) => state.city || {});
  const { projects } = useSelector((state) => state.projects || {});

  useEffect(() => {
    if (isEditMode) dispatch(viewDeal(id));
  }, [id, dispatch, isEditMode]);

  useEffect(() => {
    if (isEditMode && deal?.data) {
      const d = deal.data;
      const formattedData = {
        name: d.name || "",
        image: d.image || "",
        title: d.title || "",
        builder_id: d.builder?._id || "",
        discount_type: d.discount_type || "",
        deal_type: d.deal_type || "",
        country_id: typeof d.country === "object" ? d.country?._id || "" : d.country || "",
        state: typeof d.state === "object" ? d.state?._id || "" : d.state || "",
        city: typeof d.city === "object" ? d.city?._id || "" : d.city || "",
        project_id: d.project?._id || "",
        value: d.value || "",
        start_date: d.start_date?.split("T")[0] || "",
        end_date: d.end_date?.split("T")[0] || "",
        term_and_condition: d.term_and_condition || "",
        is_special: d.is_special || false,
      };
      setDealsData(formattedData);
      setOriginalDealData(formattedData);
    }
  }, [deal, isEditMode]);

  useEffect(() => {
    const statusEnums = enumsData?.data?.ProjectStatus
      ? Object.values(enumsData.data.ProjectStatus)
      : [];
    if (statusEnums.length && dealsData.builder_id) {
      dispatch(
        getProjectList({
          page: 1,
          limit: 500,
          status: statusEnums,
          is_deleted: false,
          builder_id: dealsData.builder_id,
        })
      );
    }
  }, [enumsData?.data?.ProjectStatus, dealsData.builder_id, dispatch]);

  useEffect(() => {
    dispatch(getEnums());
    dispatch(viewBuilderList({ page: 1, limit: 1000, is_deleted: false }));
  }, [dispatch]);

  useEffect(() => {
    if (dealsData.country_id) {
      dispatch(viewStateList({ page: 1, limit: 1000, country: dealsData.country_id }));
    }
  }, [dealsData.country_id, dispatch]);

  useEffect(() => {
    if (dealsData.state) {
      dispatch(
        viewCityList({
          page: 1,
          limit: 1000,
          country: dealsData.country_id,
          state: dealsData.state,
        })
      );
    }
  }, [dealsData.state, dealsData.country_id, dispatch]);

  const clearError = (name) => {
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddChange = (e) => {
    const { name, type, value, checked } = e.target;
    clearError(name);
    setDealsData((prev) => {
      let updated = { ...prev, [name]: type === "checkbox" ? checked : value };
      // Reset dependent dropdowns
      if (name === "country_id") {
        updated.state = "";
        updated.city = "";
      }
      if (name === "deal_type") {
        updated.builder_id = "";
        updated.project_id = "";
      }
      if (name === "builder_id") {
        updated.project_id = "";
      }
      if (name === "state") {
        updated.city = "";
      }
      if (
        name === "start_date" &&
        updated.end_date &&
        new Date(updated.end_date) < new Date(value)
      ) {
        updated.end_date = value;
      }
      return updated;
    });
  };

  const handleImageUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    const responseUrl = await AddImage(uploadedFile);
    if (responseUrl) {
      setDealsData((prev) => ({ ...prev, image: responseUrl }));
      clearError("image");
    }
    e.target.value = "";
  };

  const getUpdatedFields = () => {
    const updatedFields = {};
    for (const key in dealsData) {
      if (dealsData[key] !== originalDealData[key]) {
        updatedFields[key] = dealsData[key];
      }
    }
    return updatedFields;
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errors = {};

    // Deal Configuration
    if (!dealsData.deal_type) errors.deal_type = "Deal type is required";
    if (!dealsData.builder_id) errors.builder_id = "Builder is required";
    if (dealsData.deal_type === "deals" && !dealsData.project_id)
      errors.project_id = "Project is required for this deal type";

    // Basic Information
    if (!dealsData.name.trim()) errors.name = "Deal name is required";
    if (!dealsData.discount_type) errors.discount_type = "Discount type is required";
    if (!dealsData.value || Number(dealsData.value) <= 0)
      errors.value = "Valid discount value is required";
    if (!dealsData.term_and_condition.trim())
      errors.term_and_condition = "Terms & condition is required";

    // Validity Period
    if (!dealsData.start_date) errors.start_date = "Start date is required";
    if (!dealsData.end_date) errors.end_date = "End date is required";
    if (
      dealsData.start_date &&
      dealsData.end_date &&
      new Date(dealsData.end_date) < new Date(dealsData.start_date)
    ) {
      errors.end_date = "End date cannot be before start date";
    }

    // Location
    if (!dealsData.country_id) errors.country_id = "Country is required";
    if (!dealsData.state) errors.state = "State is required";
    if (!dealsData.city) errors.city = "City is required";

    // Image
    if (!dealsData.image) errors.image = "Deal image is required";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditMode) {
      const errors = validate();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        toast.error("Please fix the highlighted fields");
        return;
      }
    }

    try {
      let response;
      if (isEditMode) {
        const updatedFields = getUpdatedFields();
        if (Object.keys(updatedFields).length === 0) {
          toast("No changes detected.");
          return;
        }
        if (updatedFields.deal_type) {
          updatedFields.type = updatedFields.deal_type;
          delete updatedFields.deal_type;
        }
        response = await dispatch(updateDeal({ id, ...updatedFields }));
      } else {
        const payload = { ...dealsData };
        if (payload.deal_type) {
          payload.type = payload.deal_type;
          delete payload.deal_type;
        }
        response = await dispatch(addNewDeal(payload));
      }

      if (response?.statusCode === 200) {
        toast.success(isEditMode ? "Deal Updated" : "Deal Created");
        navigate("/dealsAndOffers");
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.message || "Error occurred");
    }
  };

  const discountOptions = enumsData?.data?.DiscountType
    ? Object.values(enumsData.data.DiscountType)
    : [];

  const dealTypeOptions = enumsData?.data?.dealType
    ? Object.values(enumsData.data.dealType)
    : [];

  const isFormEmpty = () =>
    !dealsData.deal_type &&
    !dealsData.builder_id &&
    !dealsData.name &&
    !dealsData.start_date &&
    !dealsData.end_date &&
    !dealsData.country_id &&
    !dealsData.state &&
    !dealsData.city &&
    !dealsData.discount_type &&
    !dealsData.value &&
    !dealsData.term_and_condition &&
    !dealsData.image;

  return (
    <MainLayout>
      <div className="df-wrapper">

        {/* ── Header ── */}
        <div className="df-header">
          <div className="df-header-left">
            <div className="df-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
            </div>
            <div>
              <h1 className="df-title">{isEditMode ? "Edit Deal" : "Add Deal"}</h1>
              <p className="df-subtitle">{isEditMode ? "Update the deal details below" : "Fill in the details to create a new deal"}</p>
            </div>
          </div>
          <button className="df-view-btn" type="button" onClick={() => navigate("/dealsAndOffers")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
            </svg>
            View Deals List
          </button>
        </div>

        {/* ── Form ── */}
        <form className="df-form" onSubmit={handleSubmit}>

          {/* ── Card 1: Deal Configuration ── */}
          <div className="df-card">
            <div className="df-card-header">
              <span className="df-card-badge">01</span>
              <h2 className="df-card-title">Deal Configuration</h2>
            </div>

            <div className="df-grid df-grid-1" style={{ marginBottom: "18px" }}>
              <div className="df-field">
                <label className="df-label">Select Deal Type <span className="df-req">*</span></label>
                <SelectInput
                  name="deal_type"
                  value={dealsData.deal_type}
                  onChange={handleAddChange}
                  placeholder="Select Deal Type"
                  options={dealTypeOptions?.map((type) => ({ label: type.replace(/_/g, " "), value: type })) || []}
                  className={fieldErrors.deal_type ? "df-select--error" : ""}
                />
                {fieldErrors.deal_type && <span className="df-error-msg">{fieldErrors.deal_type}</span>}
              </div>
            </div>

            {/* Builder only (non-deals type) */}
            {dealsData.deal_type !== "deals" && dealsData.deal_type && (
              <div className="df-grid df-grid-1">
                <div className="df-field">
                  <label className="df-label">Select Builder <span className="df-req">*</span></label>
                  <SelectInput
                    name="builder_id"
                    value={dealsData.builder_id}
                    onChange={handleAddChange}
                    placeholder="Select Builder"
                    options={builders?.data?.map((b) => ({ label: b.brand_name, value: b._id })) || []}
                    className={fieldErrors.builder_id ? "df-select--error" : ""}
                  />
                  {fieldErrors.builder_id && <span className="df-error-msg">{fieldErrors.builder_id}</span>}
                </div>
              </div>
            )}

            {/* Builder + Project (deals type) */}
            {dealsData.deal_type === "deals" && (
              <div className="df-grid df-grid-2">
                <div className="df-field">
                  <label className="df-label">Select Builder <span className="df-req">*</span></label>
                  <SelectInput
                    name="builder_id"
                    value={dealsData.builder_id}
                    onChange={handleAddChange}
                    placeholder="Select Builder"
                    options={builders?.data?.map((b) => ({ label: b.brand_name, value: b._id })) || []}
                    className={fieldErrors.builder_id ? "df-select--error" : ""}
                  />
                  {fieldErrors.builder_id && <span className="df-error-msg">{fieldErrors.builder_id}</span>}
                </div>
                <div className="df-field">
                  <label className="df-label">Select Project <span className="df-req">*</span></label>
                  <SelectInput
                    name="project_id"
                    value={dealsData.project_id}
                    onChange={handleAddChange}
                    placeholder="Select Project"
                    disabled={!dealsData.builder_id}
                    options={projects?.data?.map((p) => ({ label: p.project_name, value: p._id })) || []}
                    className={fieldErrors.project_id ? "df-select--error" : ""}
                  />
                  {fieldErrors.project_id && <span className="df-error-msg">{fieldErrors.project_id}</span>}
                </div>
              </div>
            )}
          </div>

          {/* ── Card 2: Basic Info ── */}
          <div className="df-card">
            <div className="df-card-header">
              <span className="df-card-badge">02</span>
              <h2 className="df-card-title">Basic Information</h2>
            </div>
            <div className="df-grid df-grid-2">
              <div className="df-field">
                <label className="df-label">Deal Name <span className="df-req">*</span></label>
                <TextInput
                  name="name"
                  type="text"
                  value={dealsData.name}
                  onChange={handleAddChange}
                  placeholder="Enter Name"
                  className={fieldErrors.name ? "df-input--error" : ""}
                />
                {fieldErrors.name && <span className="df-error-msg">{fieldErrors.name}</span>}
              </div>

              <div className="df-field">
                <label className="df-label">Discount Type <span className="df-req">*</span></label>
                <SelectInput
                  name="discount_type"
                  value={dealsData.discount_type}
                  onChange={handleAddChange}
                  placeholder="Select Discount Type"
                  options={discountOptions?.map((type) => ({ label: type, value: type })) || []}
                  className={fieldErrors.discount_type ? "df-select--error" : ""}
                />
                {fieldErrors.discount_type && <span className="df-error-msg">{fieldErrors.discount_type}</span>}
              </div>

              <div className="df-field">
                <label className="df-label">Discount Value <span className="df-req">*</span></label>
                <TextInput
                  name="value"
                  type="number"
                  value={dealsData.value}
                  onChange={handleAddChange}
                  placeholder="Enter discount value"
                  className={fieldErrors.value ? "df-input--error" : ""}
                />
                {fieldErrors.value && <span className="df-error-msg">{fieldErrors.value}</span>}
              </div>

              <div className="df-field">
                <label className="df-label">Terms &amp; Condition <span className="df-req">*</span></label>
                <TextInput
                  name="term_and_condition"
                  type="text"
                  value={dealsData.term_and_condition}
                  onChange={handleAddChange}
                  placeholder="Enter terms & condition"
                  className={fieldErrors.term_and_condition ? "df-input--error" : ""}
                />
                {fieldErrors.term_and_condition && <span className="df-error-msg">{fieldErrors.term_and_condition}</span>}
              </div>

              <div className="df-field">
                <label className="df-label">Is Special</label>
                <div className="df-toggle-wrapper">
                  <label className="df-toggle">
                    <input
                      type="checkbox"
                      name="is_special"
                      checked={dealsData.is_special}
                      onChange={handleAddChange}
                    />
                    <span className="df-toggle-track">
                      <span className="df-toggle-thumb" />
                    </span>
                    <span className="df-toggle-label">
                      {dealsData.is_special ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* ── Card 3: Validity ── */}
          <div className="df-card">
            <div className="df-card-header">
              <span className="df-card-badge">03</span>
              <h2 className="df-card-title">Validity Period</h2>
            </div>
            <div className="df-grid df-grid-2">
              <div className="df-field">
                <label className="df-label">Start Date <span className="df-req">*</span></label>
                <TextInput
                  name="start_date"
                  type="date"
                  value={dealsData.start_date}
                  onChange={handleAddChange}
                  min={new Date().toISOString().split("T")[0]}
                  max={dealsData.end_date}
                  className={fieldErrors.start_date ? "df-input--error" : ""}
                />
                {fieldErrors.start_date && <span className="df-error-msg">{fieldErrors.start_date}</span>}
              </div>
              <div className="df-field">
                <label className="df-label">End Date <span className="df-req">*</span></label>
                <TextInput
                  name="end_date"
                  type="date"
                  value={dealsData.end_date}
                  onChange={handleAddChange}
                  min={dealsData.start_date || new Date().toISOString().split("T")[0]}
                  className={fieldErrors.end_date ? "df-input--error" : ""}
                />
                {fieldErrors.end_date && <span className="df-error-msg">{fieldErrors.end_date}</span>}
              </div>
            </div>
          </div>

          {/* ── Card 4: Location ── */}
          <div className="df-card">
            <div className="df-card-header">
              <span className="df-card-badge">04</span>
              <h2 className="df-card-title">Location</h2>
            </div>
            <div className="df-grid df-grid-3">
              <div className="df-field">
                <label className="df-label">Country <span className="df-req">*</span></label>
                <SelectInput
                  name="country_id"
                  value={dealsData.country_id}
                  onChange={handleAddChange}
                  placeholder="Select Country"
                  options={countryList?.data?.map((c) => ({ label: c.country_name, value: c._id })) || []}
                  className={fieldErrors.country_id ? "df-select--error" : ""}
                />
                {fieldErrors.country_id && <span className="df-error-msg">{fieldErrors.country_id}</span>}
              </div>

              <div className="df-field">
                <label className={`df-label ${!dealsData.country_id ? "df-label--disabled" : ""}`}>
                  State <span className="df-req">*</span>
                </label>
                <SelectInput
                  name="state"
                  value={dealsData.state}
                  onChange={handleAddChange}
                  disabled={!dealsData.country_id}
                  placeholder={!dealsData.country_id ? "Select country first" : "Select State"}
                  options={states?.data?.map((s) => ({ label: s.state_name, value: s._id })) || []}
                  className={fieldErrors.state ? "df-select--error" : ""}
                />
                {fieldErrors.state && <span className="df-error-msg">{fieldErrors.state}</span>}
              </div>

              <div className="df-field">
                <label className={`df-label ${!dealsData.state ? "df-label--disabled" : ""}`}>
                  City <span className="df-req">*</span>
                </label>
                <SelectInput
                  name="city"
                  value={dealsData.city}
                  onChange={handleAddChange}
                  disabled={!dealsData.state}
                  placeholder={!dealsData.state ? "Select state first" : "Select City"}
                  options={cities?.data?.map((c) => ({ label: c.city_name, value: c._id })) || []}
                  className={fieldErrors.city ? "df-select--error" : ""}
                />
                {fieldErrors.city && <span className="df-error-msg">{fieldErrors.city}</span>}
              </div>
            </div>
          </div>

          {/* ── Card 5: Media Upload ── */}
          <div className="df-card">
            <div className="df-card-header">
              <span className="df-card-badge">05</span>
              <h2 className="df-card-title">Deal Image <span className="df-req">*</span></h2>
            </div>

            <input
              type="file"
              name="image"
              id="df-image-upload"
              onChange={handleImageUpload}
              hidden
            />

            <label
              className={`df-upload-zone ${fieldErrors.image ? "df-upload-zone--error" : ""}`}
              htmlFor="df-image-upload"
            >
              {dealsData.image ? (
                <span className="df-upload-success">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Image uploaded successfully
                </span>
              ) : (
                <>
                  <div className="df-upload-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <span className="df-upload-text">Click to upload deal image</span>
                  <span className="df-upload-hint">JPG, PNG, WebP supported</span>
                </>
              )}
            </label>
            {fieldErrors.image && <span className="df-error-msg" style={{ marginTop: "6px" }}>{fieldErrors.image}</span>}

            <ImageShowerModalGlobal
              file={dealsData.image}
              keyField="image"
              setState={setDealsData}
              state={dealsData}
            />
          </div>

          {/* ── Footer ── */}
          <div className="df-footer">
            <Link to="/dealsAndOffers">
              <button type="button" className="df-btn df-btn--cancel">Cancel</button>
            </Link>
            <button
              type="submit"
              className={`df-btn ${isFormEmpty() && !isEditMode ? "df-btn--disabled" : "df-btn--submit"}`}
              disabled={isFormEmpty() && !isEditMode}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </form>
      </div>
    </MainLayout>
  );
};

export default DealForm;